import { PropsWithChildren, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import AppsIcon from '@material-ui/icons/Apps';
import LibraryAddIcon from '@material-ui/icons/LibraryAdd';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import PersonIcon from '@material-ui/icons/Person';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { parseEntityRef } from '@backstage/catalog-model';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { usePermission } from '@backstage/plugin-permission-react';
import { catalogHealthReadPermission } from '../../permissions';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link
        to="/"
        underline="none"
        className={classes.link}
        aria-label="Accueil"
      >
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

// Hidden while loading (usePermission's stale-while-revalidate keeps this
// from flickering) and hidden entirely when denied — RequirePermission on
// the route itself handles the direct-navigation case.
const CatalogHealthSidebarItem = () => {
  const { loading, allowed } = usePermission({
    permission: catalogHealthReadPermission,
  });

  if (loading || !allowed) {
    return null;
  }

  return (
    <SidebarItem
      icon={ReportProblemIcon}
      to="/catalog-health"
      text="État du catalogue"
    />
  );
};

// Links to the signed-in user's own User entity page. Hidden until the
// identity resolves, so there's never a link to a wrong or empty profile.
const MyProfileSidebarItem = () => {
  const identityApi = useApi(identityApiRef);
  const [profilePath, setProfilePath] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    identityApi.getBackstageIdentity().then(({ userEntityRef }) => {
      if (cancelled) {
        return;
      }
      const { kind, namespace, name } = parseEntityRef(userEntityRef);
      setProfilePath(
        `/catalog/${namespace}/${kind.toLocaleLowerCase('en-US')}/${name}`,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  if (!profilePath) {
    return null;
  }

  return <SidebarItem icon={PersonIcon} to={profilePath} text="Mon profil" />;
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Recherche" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Accueil" />
        {/* Explorer leads to the reworked search page rather than the raw
            catalog table, which has no nav entry (reachable by URL or entity links) */}
        <SidebarItem icon={AppsIcon} to="/search" text="Explorer" />
        <MyProfileSidebarItem />
        {/* One entry: /contribuer classifies the contribution by its
            link, not by the tool it comes from */}
        <SidebarItem icon={LibraryAddIcon} to="/contribuer" text="Contribuer" />
        {/* End global nav */}
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <CatalogHealthSidebarItem />
      {/* This repo's docs/, published in TechDocs via catalog-info.yaml */}
      <SidebarItem
        icon={HelpOutlineIcon}
        to="/docs/default/component/backstage-source"
        text="Aide"
      />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Paramètres"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
